<script setup lang="ts">
import { UseElementBounding, vOnClickOutside } from '@vueuse/components';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import type { TClassBinding } from '~/interfaces/components/general';

interface IClasses {
  root?: TClassBinding;
  header?: TClassBinding;
  title?: TClassBinding;
  icon?: TClassBinding;
  iconContainer?: TClassBinding;
  content?: TClassBinding;
}

const props = withDefaults(
  defineProps<{
    defaultOpen?: boolean;
    title?: string | null;
    closeOnClickOutside?: boolean;
    classes?: IClasses | null;
    rotateDegrees?: number;
    overrideStyles?: boolean;
    bgLayer?: string;
    size?: 'sm' | 'md' | 'lg';
    useBorderDivider?: boolean;
    useGap?: boolean;
    id?: string;
    useFloatingOverlay?: boolean;
  }>(),
  {
    defaultOpen: false,
    title: null,
    closeOnClickOutside: true,
    classes: null,
    rotateDegrees: 180,
    overrideStyles: false,
    bgLayer: 'bg-layer',
    size: 'md',
    useBorderDivider: false,
    useGap: false,
    id: undefined,
    useFloatingOverlay: false,
  },
);

const modelValue = defineModel<boolean>('controller', { default: null });

const emits = defineEmits<{
  (e: 'open'): void;
  (e: 'close'): void;
  (e: 'toggle', value: boolean): void;
}>();

const { $t } = useNuxtApp();
const isControlled = computed(() => modelValue.value !== undefined);
const internalOpen = ref<boolean>(modelValue.value ?? props.defaultOpen);
const accordionContainer = ref<HTMLElement>(null);

watch(
  () => modelValue.value,
  (val) => {
    if (val !== undefined) internalOpen.value = !!val;
  },
);

const setOpen = (next: boolean) => {
  if (internalOpen.value === next) return;
  if (isControlled.value) modelValue.value = next;
  else internalOpen.value = next;

  emits('toggle', next);
  if (next) emits('open');
  else emits('close');
};

const onSummaryClick = (e: MouseEvent) => {
  e.preventDefault();
  setOpen(!internalOpen.value);
};

const onDetailsToggle = (e: Event) => {
  const details = e.currentTarget as HTMLDetailsElement;
  if (details) {
    const next = !!details.open;
    if (isControlled.value) {
      details.open = !!internalOpen.value;
      return;
    }
    if (next !== internalOpen.value) setOpen(next);
  }
};

const onClickOutside = () => {
  if (!props.closeOnClickOutside) return;
  if (internalOpen.value) setOpen(false);
};

const rotationStyle = computed(() => ({
  transform: `rotate(${internalOpen.value ? props.rotateDegrees : 0}deg)`,
}));

const mergeClassBinding = (
  base?: TClassBinding,
  extra?: TClassBinding,
): TClassBinding => {
  if (!base) return extra ?? '';
  if (!extra) return base;
  return [base, extra];
};
const styleSizeMap = {
  sm: {
    header: 'rounded-sm',
    content: 'rounded-b-sm',
  },
  md: { header: 'rounded-md', content: 'rounded-b-md' },
  lg: {
    header: 'rounded-lg',
    content: 'rounded-b-lg',
  },
};

const defaultClasses = computed<IClasses>(() => ({
  root: {
    'border-none rounded-lg bg-layer': true,
    'mb-2 md:mb-4': props.useGap,
  },
  header: {
    [`text-base-priority flex items-center justify-between w-full cursor-pointer select-none p-2 md:px-3 ${props.bgLayer}`]:
      true,
    [`rounded-b-none ${styleSizeMap[props.size].header}`]: internalOpen.value,
    [`${styleSizeMap[props.size].header}`]: !internalOpen.value,
    'border-b bg-layer-border': internalOpen.value && props.useBorderDivider,
  },
  title: 'text-base font-normal text-base-priority',
  icon: 'w-5 h-5 stroke-2 text-base-priority transition-transform duration-200 ease-out',
  iconContainer: 'flex items-center justify-center',
  content: `px-3 pt-2 pb-3 text-sm text-base-priority ${props.useFloatingOverlay ? 'absolute z-5' : ''} ${styleSizeMap[props.size].content} ${props.bgLayer}`,
}));

const mergedClasses = computed<IClasses>(() => {
  if (props.overrideStyles && props.classes) {
    return {
      root: props.classes.root ?? '',
      header: props.classes.header ?? '',
      title: props.classes.title ?? '',
      icon: props.classes.icon ?? '',
      iconContainer: props.classes.iconContainer ?? '', // add this
      content: props.classes.content ?? '',
    };
  }

  return {
    root: mergeClassBinding(defaultClasses.value.root, props.classes?.root),
    header: mergeClassBinding(
      defaultClasses.value.header,
      props.classes?.header,
    ),
    title: mergeClassBinding(defaultClasses.value.title, props.classes?.title),
    icon: mergeClassBinding(defaultClasses.value.icon, props.classes?.icon),
    iconContainer: mergeClassBinding(
      defaultClasses.value.iconContainer,
      props.classes?.iconContainer,
    ),
    content: mergeClassBinding(
      defaultClasses.value.content,
      props.classes?.content,
    ),
  };
});
</script>

<template>
  <UseElementBounding v-slot="{ height, width }">
    <details
      v-bind="{ ...(!!id && { id }) }"
      ref="accordionContainer"
      v-on-click-outside="onClickOutside"
      :open="internalOpen"
      :class="[mergedClasses.root]"
      @toggle="onDetailsToggle"
    >
      <summary
        :class="[mergedClasses.header]"
        :aria-expanded="internalOpen"
        @click="onSummaryClick"
      >
        <span :class="[mergedClasses.title]">
          <slot name="title">{{ $t(title) }}</slot>
        </span>

        <span :class="[mergedClasses.iconContainer]">
          <slot name="icon">
            <ChevronDownIcon
              :class="[mergedClasses.icon]"
              :style="rotationStyle"
            />
          </slot>
        </span>
      </summary>

      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-show="internalOpen"
          :class="[mergedClasses.content]"
          v-bind="{
            ...(useFloatingOverlay && {
              style: {
                top: `${height - 6}px`,
                width: `${width}px`,
              },
            }),
          }"
        >
          <slot />
        </div>
      </Transition>
    </details>
  </UseElementBounding>
</template>
